/**
 * What Tab does (DESIGN 6.1). During a match it is the spellbook key and never
 * moves focus, so a stray Tab cannot land on a HUD button that Space or Enter
 * would then press. Everywhere else it walks the menus like any page: on the
 * landing page, in the lobby and results, and while a dialog is open.
 */

import type { RoomPhase } from '@shared/index.js';

/**
 * The phases that are menus: the lobby and the results screen. Every other phase is a running
 * match, so a mode that adds its own phase (capture the flag's `match`) counts as one without
 * being listed here.
 */
const MENU_PHASES: ReadonlySet<RoomPhase> = new Set<RoomPhase>(['lobby', 'gameover']);

export interface TabContext {
  inRoom: boolean;
  phase: RoomPhase;
  /** A dialog the player opened is on screen (the spellbook pinned from its button). */
  menuOpen: boolean;
}

/** True when Tab should move keyboard focus instead of holding the spellbook open. */
export function tabMovesFocus(ctx: TabContext): boolean {
  if (!ctx.inRoom || ctx.menuOpen) return true;
  return MENU_PHASES.has(ctx.phase);
}

/**
 * What a Tab keydown does inside the game's key handler:
 *  - 'other': Tab is bound to something other than the spellbook; handle that binding as usual.
 *  - 'pass': a menu is up, so leave the event to the browser and focus moves.
 *  - 'swallow': mid-match and Tab (or Shift+Tab) is unbound; block it so focus cannot wander.
 *  - 'book': the spellbook key; a hold that is already running keeps its repeats even if the
 *    phase turns into a menu under it.
 */
export type TabRole = 'other' | 'pass' | 'swallow' | 'book';

export function tabKeyRole(bound: string | null | undefined, holding: boolean, walking: boolean): TabRole {
  if (bound && bound !== 'spellbook') return 'other';
  if (!holding && walking) return 'pass';
  return bound === 'spellbook' ? 'book' : 'swallow';
}

/**
 * Enter or Space on a focused button or link (a menu walked with Tab) presses it. That wins over
 * their game jobs, chat and Magick; mid-match no control keeps focus, so the game never loses them.
 */
export function pressesFocusedControl(ev: { key: string; target: unknown }): boolean {
  if (ev.key !== 'Enter' && ev.key !== ' ') return false;
  const target = ev.target as { closest?: (selector: string) => unknown } | null;
  return typeof target?.closest === 'function' && target.closest('button, a[href], summary') !== null;
}
